import Module from '../../module';
import scroll from '../../../libs/scroll/scroll';
import gsap from 'gsap';

interface OptionsInterface {
	cssId: {
		anim?: {
			investorsStockInfoCalculatorHeaderCls: string;
			investorsStockInfoCalculatorQuoteDataCls: string;
		},

		investorsStockInfoCalculatorCalculateButtonCls?: string;
		investorsStockInfoCalculatorFormId?: string;
		investorsStockInfoCalculatorInputAmount?: string;
		investorsStockInfoCalculatorDataSelectorInputStartDateCls?: string;
		investorsStockInfoCalculatorDataSelectorInputEndDateCls?: string;
		dataSelectorButtonsButtonsWrapperLoadingCls?: string;
		investorsStockInfoCalculatorQuoteDataButtonsWrapperCls?: string;
		investorsStockInfoCalculatorInputButtonsWrapperCls?: string;
		investorsStockInfoCalculatorQuoteDataInnerCls?: string;
		investorsStockInfoCalculatorCalculateButtonsWrapperCls?: string;
		quoteDataInnerLoadingCls?: string;
		investorsStockInfoCalculatorQuoteDataButtonCls?: string;
		buttonPrimaryActiveCls?: string;
		investorsStockInfoCalculatorQuoteDataStartDateCls?: string;
		investorsStockInfoCalculatorQuoteDataEndDateCls?: string;
		investorsStockInfoCalculatorQuoteDataStartPriceCls?: string;
		investorsStockInfoCalculatorQuoteDataEndPriceCls?: string;
		investorsStockInfoCalculatorQuoteDataTotalReturnCls?: string;
		investorsStockInfoCalculatorQuoteDataCompoundCls?: string;
		investorsStockInfoCalculatorQuoteDataStartInvestmentCls?: string;
		investorsStockInfoCalculatorQuoteDataEndingInvestmentCls?: string;
		investorsStockInfoCalculatorQuoteDataAreaCls?: string;
		quoteDataAreaActiveCls?: string;
	},

	apiStockEndpoint?: string;
	slug: string
}

class Calculator extends Module {

	publicVars: OptionsInterface;

	privateVars: {
		animating: boolean;
		quoteDataVisible: boolean;
		currentTicker: string | null;
		tickers: {
			plsrl: 'plsrl';
			plsrv: 'plsrv';
			psrhf: 'psrhf';
		},
		inputAmount: string;
		inputStartDate: string;
		inputEndDate: string;
	} = {
		animating: false,
		quoteDataVisible: false,
		currentTicker: null,
		tickers: {
			plsrl: 'plsrl',
			plsrv: 'plsrv',
			psrhf: 'psrhf',
		},
		inputAmount: '',
		inputStartDate: '',
		inputEndDate: ''
	};

	constructor(options: OptionsInterface) {
		super('Investors Stock Info Calculator Module');

		this.publicVars = options;
	}

	startup() {
		this.msg('startup');

		scroll.init();
		scroll.registerAnimatedElement(this.publicVars.cssId.anim?.investorsStockInfoCalculatorHeaderCls || '',
			'investors_stock_info_calculator', { animCls: 'animate__zoomIn50P', once: true });
		scroll.registerAnimatedElement(this.publicVars.cssId.anim?.investorsStockInfoCalculatorQuoteDataCls || '',
			'investors_stock_info_calculator', { animCls: 'animate__fadeIn', once: true });
	}

	bind() {
		this.msg('bind');

		const buttonsHtml = document.querySelectorAll<HTMLElement>(
			this.publicVars.cssId.investorsStockInfoCalculatorQuoteDataButtonCls || '');

		buttonsHtml.forEach(button => {
			if (button.classList.contains(this.publicVars.cssId.buttonPrimaryActiveCls?.substring(1) || '')) {
				this.privateVars.currentTicker = button?.dataset['ticker'] || 'plsrl';
			}

			button.addEventListener('click', e => {

				const target = e.currentTarget;

				if (target instanceof HTMLElement) {
					const ticker = target.dataset.ticker;

					if (ticker) {
						this.handleButtonClick(ticker);
					}
				}
			});
		});

		const calculateFormHtml = document.querySelector(
			this.publicVars.cssId.investorsStockInfoCalculatorFormId || '');

		if (calculateFormHtml) {
			calculateFormHtml.addEventListener('submit', (e) => {
				e.preventDefault();

				this.handleCalculateInvestment();
			});
		}
	}

	handleButtonClick(ticker: string) {
		if(this.privateVars.animating) {
			return;
		}

		const currentAreaHtml = document.querySelector(`${this.publicVars.cssId.investorsStockInfoCalculatorQuoteDataAreaCls}-${this.privateVars.currentTicker}`) as HTMLElement;
		const nextAreaHtml = document.querySelector(`${this.publicVars.cssId.investorsStockInfoCalculatorQuoteDataAreaCls}-${ticker}`) as HTMLElement;

		if(!currentAreaHtml || !nextAreaHtml) {
			return;
		}

		if(this.privateVars.currentTicker === ticker) {
			return;
		}

		this.privateVars.animating = true;

		const currentButtonHtml = document.querySelector<HTMLElement>(`${this.publicVars.cssId.investorsStockInfoCalculatorQuoteDataButtonCls}-${this.privateVars.currentTicker}`) as HTMLElement;
		const nextButtonHtml = document.querySelector<HTMLElement>(`${this.publicVars.cssId.investorsStockInfoCalculatorQuoteDataButtonCls}-${ticker}`) as HTMLElement;

		currentButtonHtml.classList.remove(this.publicVars.cssId.buttonPrimaryActiveCls?.substring(1) || '');
		nextButtonHtml.classList.add(this.publicVars.cssId.buttonPrimaryActiveCls?.substring(1) || '');

		if(this.privateVars.quoteDataVisible) {
			gsap.to(
				`${this.publicVars.cssId.investorsStockInfoCalculatorQuoteDataAreaCls}-${this.privateVars.currentTicker}`,
				{
					duration: 0.1,
					opacity: 0,
					onComplete: () => {
						currentAreaHtml.classList.remove(
							this.publicVars.cssId?.quoteDataAreaActiveCls?.substring(1) || '');
						nextAreaHtml.classList.add(this.publicVars.cssId?.quoteDataAreaActiveCls?.substring(1) || '');
						nextAreaHtml.style.opacity = '0';
						gsap.to(`${this.publicVars.cssId.investorsStockInfoCalculatorQuoteDataAreaCls}-${ticker}`, {
							duration: 0.2,
							opacity: 1,
							onComplete: () => {
								this.privateVars.currentTicker = ticker;
								this.privateVars.animating = false;
								nextAreaHtml.removeAttribute('style');
							}
						});
					}
				});
		} else {
			currentAreaHtml.classList.remove(
				this.publicVars.cssId?.quoteDataAreaActiveCls?.substring(1) || '');
			nextAreaHtml.classList.add(this.publicVars.cssId?.quoteDataAreaActiveCls?.substring(1) || '');
			this.privateVars.currentTicker = ticker;
			this.privateVars.animating = false;
		}
	}

	async handleCalculateInvestment() {
		const inputAmountHtml = document.querySelector(
			this.publicVars.cssId.investorsStockInfoCalculatorInputAmount || '') as HTMLInputElement;
		const inputStartDateHtml = document.querySelector(
			this.publicVars.cssId.investorsStockInfoCalculatorDataSelectorInputStartDateCls || '') as HTMLInputElement;
		const inputEndDateHtml = document.querySelector(
			this.publicVars.cssId.investorsStockInfoCalculatorDataSelectorInputEndDateCls || '') as HTMLInputElement;

		if (!inputAmountHtml || !inputStartDateHtml || !inputEndDateHtml) {
			this.errorMsg('Please configure inputs fields properly');
		}

		const quoteDataInnerHtml = document.querySelector(
			this.publicVars.cssId.investorsStockInfoCalculatorQuoteDataInnerCls || '') as HTMLElement;

		if (!quoteDataInnerHtml) {
			this.errorMsg('Please configure quote data inner table properly');
		}

		this.handleLoadingState(true);

		if (!this.privateVars.quoteDataVisible) {
			quoteDataInnerHtml.style.display = 'block';
			quoteDataInnerHtml.style.opacity = '0';
			quoteDataInnerHtml.style.height = '0';

			gsap.to(quoteDataInnerHtml, {
				opacity: 1,
				scale: 1,
				height: 'auto',
				duration: 0.3
			});

			this.privateVars.quoteDataVisible = true;
		}

		const amountToInvest = inputAmountHtml.value;
		const startDate = inputStartDateHtml.value;
		const endDate = inputEndDateHtml.value;

		if (this.privateVars.inputAmount === amountToInvest && this.privateVars.inputStartDate === startDate &&
			this.privateVars.inputEndDate === endDate) {

			setTimeout(() => {
				this.handleLoadingState(false);
			}, 500);
			return;
		}

		this.privateVars.inputAmount = amountToInvest;
		this.privateVars.inputStartDate = startDate;
		this.privateVars.inputEndDate = endDate;


		try {
			const result = await fetch(`${this.publicVars.apiStockEndpoint}/${amountToInvest}/${startDate}/${endDate}`);

			if (!result.ok) {
				this.handleLoadingState(false);
				return;
			}

			const data: {
				data: {
					metadata: {
						start_date: string,
						end_date: string,
						investment_amount: string;
					},
					plsrv: {
						start_share_price: string;
						end_share_price: string;
						total_return_percent: string;
						cagr_percent: string;
						start_investment: string;
						end_investment: string;
					},
					plsrl: {
						start_share_price: string;
						end_share_price: string;
						total_return_percent: string;
						cagr_percent: string;
						start_investment: string;
						end_investment: string;
					},
					psrhf: {
						start_share_price: string;
						end_share_price: string;
						total_return_percent: string;
						cagr_percent: string;
						start_investment: string;
						end_investment: string;
					}
				}
			} = await result.json();

			this.handleLoadingState(false);

			const stockDataQuoteDataStartDatePlsrlHtml = document.querySelector(
				`${this.publicVars.cssId.investorsStockInfoCalculatorQuoteDataStartDateCls}-${this.privateVars.tickers.plsrl}`);
			const stockDataQuoteDataEndDatePlsrlHtml = document.querySelector(
				`${this.publicVars.cssId.investorsStockInfoCalculatorQuoteDataEndDateCls}-${this.privateVars.tickers.plsrl}`);
			const stockDataQuoteDataStartPricePlsrlHtml = document.querySelector(
				`${this.publicVars.cssId.investorsStockInfoCalculatorQuoteDataStartPriceCls}-${this.privateVars.tickers.plsrl}`);
			const stockDataQuoteDataEndPricePlsrlHtml = document.querySelector(
				`${this.publicVars.cssId.investorsStockInfoCalculatorQuoteDataEndPriceCls}-${this.privateVars.tickers.plsrl}`);
			const stockDataQuoteDataTotalReturnPlsrlHtml = document.querySelector(
				`${this.publicVars.cssId.investorsStockInfoCalculatorQuoteDataTotalReturnCls}-${this.privateVars.tickers.plsrl}`);
			const stockDataQuoteDataCompoundPlsrlHtml = document.querySelector(
				`${this.publicVars.cssId.investorsStockInfoCalculatorQuoteDataCompoundCls}-${this.privateVars.tickers.plsrl}`);
			const stockDataQuoteDataStartInvestmentPlsrlHtml = document.querySelector(
				`${this.publicVars.cssId.investorsStockInfoCalculatorQuoteDataStartInvestmentCls}-${this.privateVars.tickers.plsrl}`);
			const stockDataQuoteDataEndInvestmentPlsrlHtml = document.querySelector(
				`${this.publicVars.cssId.investorsStockInfoCalculatorQuoteDataEndingInvestmentCls}-${this.privateVars.tickers.plsrl}`);

			if (stockDataQuoteDataStartDatePlsrlHtml) {
				stockDataQuoteDataStartDatePlsrlHtml.innerHTML = data.data.metadata.start_date;
			}

			if (stockDataQuoteDataEndDatePlsrlHtml) {
				stockDataQuoteDataEndDatePlsrlHtml.innerHTML = data.data.metadata.end_date;
			}

			if (stockDataQuoteDataStartPricePlsrlHtml) {
				stockDataQuoteDataStartPricePlsrlHtml.innerHTML = data.data.plsrl.start_share_price ? '£' + data.data.plsrl.start_share_price : '-';
			}

			if (stockDataQuoteDataEndPricePlsrlHtml) {
				stockDataQuoteDataEndPricePlsrlHtml.innerHTML = data.data.plsrl.end_share_price ? '£' + data.data.plsrl.end_share_price : '-';
			}

			if (stockDataQuoteDataTotalReturnPlsrlHtml) {
				stockDataQuoteDataTotalReturnPlsrlHtml.innerHTML = data.data.plsrl.total_return_percent ? data.data.plsrl.total_return_percent + '%' : '-';
			}

			if (stockDataQuoteDataCompoundPlsrlHtml) {
				stockDataQuoteDataCompoundPlsrlHtml.innerHTML = data.data.plsrl.cagr_percent ? data.data.plsrl.cagr_percent + '%': '-';
			}

			if (stockDataQuoteDataStartInvestmentPlsrlHtml) {
				stockDataQuoteDataStartInvestmentPlsrlHtml.innerHTML = data.data.plsrl.start_investment ? '£' + data.data.plsrl.start_investment : '-';
			}

			if (stockDataQuoteDataEndInvestmentPlsrlHtml) {
				stockDataQuoteDataEndInvestmentPlsrlHtml.innerHTML = data.data.plsrl.end_investment ? '£' + data.data.plsrl.end_investment : '-';
			}

			const stockDataQuoteDataStartDatePlsrvHtml = document.querySelector(
				`${this.publicVars.cssId.investorsStockInfoCalculatorQuoteDataStartDateCls}-${this.privateVars.tickers.plsrv}`);
			const stockDataQuoteDataEndDatePlsrvHtml = document.querySelector(
				`${this.publicVars.cssId.investorsStockInfoCalculatorQuoteDataEndDateCls}-${this.privateVars.tickers.plsrv}`);
			const stockDataQuoteDataStartPricePlsrvHtml = document.querySelector(
				`${this.publicVars.cssId.investorsStockInfoCalculatorQuoteDataStartPriceCls}-${this.privateVars.tickers.plsrv}`);
			const stockDataQuoteDataEndPricePlsrvHtml = document.querySelector(
				`${this.publicVars.cssId.investorsStockInfoCalculatorQuoteDataEndPriceCls}-${this.privateVars.tickers.plsrv}`);
			const stockDataQuoteDataTotalReturnPlsrvHtml = document.querySelector(
				`${this.publicVars.cssId.investorsStockInfoCalculatorQuoteDataTotalReturnCls}-${this.privateVars.tickers.plsrv}`);
			const stockDataQuoteDataCompoundPlsrvHtml = document.querySelector(
				`${this.publicVars.cssId.investorsStockInfoCalculatorQuoteDataCompoundCls}-${this.privateVars.tickers.plsrv}`);
			const stockDataQuoteDataStartInvestmentPlsrvHtml = document.querySelector(
				`${this.publicVars.cssId.investorsStockInfoCalculatorQuoteDataStartInvestmentCls}-${this.privateVars.tickers.plsrv}`);
			const stockDataQuoteDataEndInvestmentPlsrvHtml = document.querySelector(
				`${this.publicVars.cssId.investorsStockInfoCalculatorQuoteDataEndingInvestmentCls}-${this.privateVars.tickers.plsrv}`);

			if (stockDataQuoteDataStartDatePlsrvHtml) {
				stockDataQuoteDataStartDatePlsrvHtml.innerHTML = data.data.metadata.start_date;
			}

			if (stockDataQuoteDataEndDatePlsrvHtml) {
				stockDataQuoteDataEndDatePlsrvHtml.innerHTML = data.data.metadata.end_date;
			}

			if (stockDataQuoteDataStartPricePlsrvHtml) {
				stockDataQuoteDataStartPricePlsrvHtml.innerHTML = data.data.plsrv.start_share_price ? '$' + data.data.plsrv.start_share_price : '-';
			}

			if (stockDataQuoteDataEndPricePlsrvHtml) {
				stockDataQuoteDataEndPricePlsrvHtml.innerHTML = data.data.plsrv.end_share_price ? '$' + data.data.plsrv.end_share_price : '-';
			}

			if (stockDataQuoteDataTotalReturnPlsrvHtml) {
				stockDataQuoteDataTotalReturnPlsrvHtml.innerHTML = data.data.plsrv.total_return_percent ? data.data.plsrv.total_return_percent + '%' : '-';
			}

			if (stockDataQuoteDataCompoundPlsrvHtml) {
				stockDataQuoteDataCompoundPlsrvHtml.innerHTML = data.data.plsrv.cagr_percent ? data.data.plsrv.cagr_percent + '%' : '-';
			}

			if (stockDataQuoteDataStartInvestmentPlsrvHtml) {
				stockDataQuoteDataStartInvestmentPlsrvHtml.innerHTML = data.data.plsrv.start_investment ? '$' + data.data.plsrv.start_investment : '-';
			}

			if (stockDataQuoteDataEndInvestmentPlsrvHtml) {
				stockDataQuoteDataEndInvestmentPlsrvHtml.innerHTML = data.data.plsrv.end_investment ? '$' + data.data.plsrv.end_investment : '-';
			}

			const stockDataQuoteDataStartDatePsrhfHtml = document.querySelector(
				`${this.publicVars.cssId.investorsStockInfoCalculatorQuoteDataStartDateCls}-${this.privateVars.tickers.psrhf}`);
			const stockDataQuoteDataEndDatePsrhfHtml = document.querySelector(
				`${this.publicVars.cssId.investorsStockInfoCalculatorQuoteDataEndDateCls}-${this.privateVars.tickers.psrhf}`);
			const stockDataQuoteDataStartPricePsrhfHtml = document.querySelector(
				`${this.publicVars.cssId.investorsStockInfoCalculatorQuoteDataStartPriceCls}-${this.privateVars.tickers.psrhf}`);
			const stockDataQuoteDataEndPricePsrhfHtml = document.querySelector(
				`${this.publicVars.cssId.investorsStockInfoCalculatorQuoteDataEndPriceCls}-${this.privateVars.tickers.psrhf}`);
			const stockDataQuoteDataTotalReturnPsrhfHtml = document.querySelector(
				`${this.publicVars.cssId.investorsStockInfoCalculatorQuoteDataTotalReturnCls}-${this.privateVars.tickers.psrhf}`);
			const stockDataQuoteDataCompoundPsrhfHtml = document.querySelector(
				`${this.publicVars.cssId.investorsStockInfoCalculatorQuoteDataCompoundCls}-${this.privateVars.tickers.psrhf}`);
			const stockDataQuoteDataStartInvestmentPsrhfHtml = document.querySelector(
				`${this.publicVars.cssId.investorsStockInfoCalculatorQuoteDataStartInvestmentCls}-${this.privateVars.tickers.psrhf}`);
			const stockDataQuoteDataEndInvestmentPsrhfHtml = document.querySelector(
				`${this.publicVars.cssId.investorsStockInfoCalculatorQuoteDataEndingInvestmentCls}-${this.privateVars.tickers.psrhf}`);

			if (stockDataQuoteDataStartDatePsrhfHtml) {
				stockDataQuoteDataStartDatePsrhfHtml.innerHTML = data.data.metadata.start_date;
			}

			if (stockDataQuoteDataEndDatePsrhfHtml) {
				stockDataQuoteDataEndDatePsrhfHtml.innerHTML = data.data.metadata.end_date;
			}

			if (stockDataQuoteDataStartPricePsrhfHtml) {
				stockDataQuoteDataStartPricePsrhfHtml.innerHTML = data.data.psrhf.start_share_price ? '$' + data.data.psrhf.start_share_price : '-';
			}

			if (stockDataQuoteDataEndPricePsrhfHtml) {
				stockDataQuoteDataEndPricePsrhfHtml.innerHTML = data.data.psrhf.end_share_price ? '$' + data.data.psrhf.end_share_price : '-';
			}

			if (stockDataQuoteDataTotalReturnPsrhfHtml) {
				stockDataQuoteDataTotalReturnPsrhfHtml.innerHTML = data.data.psrhf.total_return_percent ? data.data.psrhf.total_return_percent + '%' : '-';
			}

			if (stockDataQuoteDataCompoundPsrhfHtml) {
				stockDataQuoteDataCompoundPsrhfHtml.innerHTML = data.data.psrhf.cagr_percent ? data.data.psrhf.cagr_percent + '%' : '-';
			}

			if (stockDataQuoteDataStartInvestmentPsrhfHtml) {
				stockDataQuoteDataStartInvestmentPsrhfHtml.innerHTML = data.data.psrhf.start_investment ? '$' + data.data.psrhf.start_investment : '-';
			}

			if (stockDataQuoteDataEndInvestmentPsrhfHtml) {
				stockDataQuoteDataEndInvestmentPsrhfHtml.innerHTML = data.data.psrhf.end_investment ? '$' + data.data.psrhf.end_investment : '-';
			}

		} catch (e) {
			this.handleLoadingState(false);
			return;
		}
	}

	handleLoadingState(loading: boolean) {
		const quoteDataInnerHtml = document.querySelector(
			this.publicVars.cssId.investorsStockInfoCalculatorQuoteDataInnerCls || '') as HTMLElement;
		const buttonsWrapperHtml = document.querySelector(
			this.publicVars.cssId.investorsStockInfoCalculatorQuoteDataButtonsWrapperCls || '');
		const inputWrapperHtml = document.querySelector(
			this.publicVars.cssId.investorsStockInfoCalculatorInputButtonsWrapperCls || '');
		const calculateButtonsWrapperHtml = document.querySelector(
			this.publicVars.cssId.investorsStockInfoCalculatorCalculateButtonsWrapperCls || '');

		if (quoteDataInnerHtml && buttonsWrapperHtml && inputWrapperHtml && calculateButtonsWrapperHtml) {
			if (loading) {
				quoteDataInnerHtml.classList.add(this.publicVars.cssId.quoteDataInnerLoadingCls?.substring(1) || '');
				buttonsWrapperHtml.classList.add(
					this.publicVars.cssId.dataSelectorButtonsButtonsWrapperLoadingCls?.substring(1) || '');
				inputWrapperHtml.classList.add(
					this.publicVars.cssId.dataSelectorButtonsButtonsWrapperLoadingCls?.substring(1) || '');
				calculateButtonsWrapperHtml.classList.add(
					this.publicVars.cssId.dataSelectorButtonsButtonsWrapperLoadingCls?.substring(1) || '');
			} else {
				quoteDataInnerHtml.classList.remove(this.publicVars.cssId.quoteDataInnerLoadingCls?.substring(1) || '');
				buttonsWrapperHtml.classList.remove(
					this.publicVars.cssId.dataSelectorButtonsButtonsWrapperLoadingCls?.substring(1) || '');
				inputWrapperHtml.classList.remove(
					this.publicVars.cssId.dataSelectorButtonsButtonsWrapperLoadingCls?.substring(1) || '');
				calculateButtonsWrapperHtml.classList.remove(
					this.publicVars.cssId.dataSelectorButtonsButtonsWrapperLoadingCls?.substring(1) || '');
			}
		}
	}
}

export default Calculator;